import { Component, ElementRef, effect, input, output, viewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ButtonComponent, IconName, EmptyComponent, IconComponent } from 'nebula-ui-kit';

import { FileNode } from '@nebula/shared-vault/domain';

function normalizeRepoPath(path: string | null | undefined): string {
  return (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
}

@Component({
  selector: 'app-folder-actions',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, ButtonComponent, EmptyComponent, IconComponent],
  template: `
    <div class="folder-content">
      <div class="folder-header">
        <div class="folder-title">
          <lib-icon class="folder-header-icon" name="Folder" [size]="24"></lib-icon>
          <h3 [attr.title]="folderName()">{{ folderName() }}</h3>
        </div>
        <div class="folder-stats">
          <span>{{ files().length }} archivos</span>
          <span>{{ totalSize() }}</span>
        </div>
      </div>

      <div class="files-list-container">
        <div class="files-list">
          @for (file of files(); track file.path) {
            <div
              #fileCard
              class="file-row"
              [class.file-row--selected]="isSelected(file)"
              (click)="onOpenFile(file)"
              (keydown.enter)="onOpenFile(file)"
              (keydown.space)="onOpenFile(file)"
              tabindex="0"
              role="button"
            >
              <div class="file-preview">
                <lib-icon
                  class="file-type-icon"
                  [name]="getFileIcon(file)"
                  [size]="28"
                  [color]="fileCardIconColor"
                ></lib-icon>
              </div>

              <div class="file-info">
                <div class="file-name" [attr.title]="file.name">
                  {{ file.name }}
                </div>
                <div class="file-meta">
                  <span class="file-size">{{ formatFileSize(file.size) }}</span>
                </div>
              </div>

              <div
                class="file-actions"
                role="toolbar"
                aria-label="Acciones del archivo"
                (click)="$event.stopPropagation()"
              >
                <span
                  class="file-action-tooltip-host"
                  [attr.title]="null"
                  [matTooltip]="openTooltip(file)"
                  matTooltipClass="lib-custom-tooltip"
                  matTooltipPosition="above"
                  matTooltipShowDelay="200"
                >
                  <lib-button
                    variant="icon-ghost"
                    prefixIcon="Eye"
                    [iconSize]="20"
                    ariaLabel="Abrir vista previa"
                    type="button"
                    (clicked)="onOpenFile(file)"
                  />
                </span>
                <span
                  class="file-action-tooltip-host"
                  [attr.title]="null"
                  [matTooltip]="tooltipDescargar"
                  matTooltipClass="lib-custom-tooltip"
                  matTooltipPosition="above"
                  matTooltipShowDelay="200"
                >
                  <lib-button
                    variant="icon-ghost"
                    prefixIcon="Download"
                    [iconSize]="20"
                    ariaLabel="Descargar archivo"
                    type="button"
                    (clicked)="onDownloadFile(file)"
                  />
                </span>
                @if (canAssignFile(file)) {
                  <span
                    class="file-action-tooltip-host"
                    [attr.title]="null"
                    [matTooltip]="tooltipAsignar"
                    matTooltipClass="lib-custom-tooltip"
                    matTooltipPosition="above"
                    matTooltipShowDelay="200"
                    (click)="$event.stopPropagation()"
                    (keydown.enter)="$event.stopPropagation()"
                    (keydown.space)="$event.stopPropagation()"
                  >
                    <lib-button
                      variant="icon-ghost"
                      prefixIcon="UserCheck"
                      [iconSize]="20"
                      ariaLabel="Asignar archivo"
                      type="button"
                      (clicked)="onAssignFile(file)"
                    />
                  </span>
                }
              </div>
            </div>
          } @empty {
            <div class="empty-state">
              <lib-empty
                [message]="'Carpeta vacia'"
                [description]="'No hay archivos en esta carpeta'"
              >
              </lib-empty>
            </div>
          }
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
        min-height: 0;
        flex: 1 1 auto;
      }

      .folder-content {
        height: 100%;
        display: flex;
        flex-direction: column;
        min-height: 0;
        background: var(--bg-primary, #ffffff);
      }

      .folder-header {
        flex-shrink: 0;
        padding: 1rem 1.25rem;
        border-bottom: 1px solid var(--border-color, #e5e7eb);

        .folder-title {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 8px;
          min-width: 0;

          .folder-header-icon {
            color: var(--md-sys-color-primary, #0e58a9);
            flex-shrink: 0;
            margin-top: 0.1rem;
          }

          h3 {
            margin: 0;
            min-width: 0;
            flex: 1 1 auto;
            font-size: 1.125rem;
            font-weight: 600;
            line-height: 1.35;
            color: var(--text-primary, #111827);
            overflow: hidden;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
        }

        .folder-stats {
          display: flex;
          flex-wrap: wrap;
          gap: 16px 24px;
          font-size: 0.875rem;
          color: var(--text-gray, #6b7280);
        }
      }

      .files-list-container {
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      .files-list {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding: 0.75rem 1rem 1.25rem;
        display: flex;
        flex-direction: column;
        gap: 6px;
        width: 100%;
        box-sizing: border-box;
      }

      .file-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
        min-width: 0;
        padding: 8px 12px;
        background: var(--bg-primary, #ffffff);
        border: 1px solid var(--border-color, #e5e7eb);
        border-radius: 10px;
        box-sizing: border-box;
        transition: box-shadow 0.15s ease, border-color 0.15s ease, background-color 0.15s ease;
        cursor: pointer;
      }

      .file-row:hover {
        border-color: var(--md-sys-color-primary, #0e58a9);
        background: color-mix(in srgb, var(--md-sys-color-primary, #0e58a9) 4%, transparent);
      }

      .file-row.file-row--selected {
        border-width: 2px;
        border-color: var(--md-sys-color-primary, #0e58a9);
        box-shadow: 0 0 0 2px rgba(14, 88, 169, 0.15);
        padding: 7px 11px;
      }

      :host-context(.dark-theme) .file-row {
        background: var(--bg-primary, #1e1e1e);
        border-color: var(--border-color, #333);
      }

      :host-context(.dark-theme) .file-row:hover {
        border-color: var(--md-sys-color-primary, #9ecaff);
      }

      .file-preview {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 2rem;
        height: 2rem;
      }

      .file-preview .file-type-icon {
        color: var(--md-sys-color-primary, #0e58a9);
      }

      .file-info {
        flex: 1 1 auto;
        min-width: 0;
      }

      .file-name {
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.35;
        color: var(--text-primary, #111827);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .file-meta {
        margin-top: 0.15rem;
        font-size: 0.75rem;
        color: var(--text-gray, #6b7280);
      }

      .file-actions {
        display: flex;
        flex-wrap: nowrap;
        justify-content: flex-end;
        align-items: center;
        gap: 2px;
        flex-shrink: 0;
        margin-left: auto;
        color: var(--md-sys-color-primary, #0e58a9);
      }

      .file-action-tooltip-host {
        display: inline-flex;
        flex-shrink: 0;
        line-height: 0;
        vertical-align: middle;
      }

      .file-actions lib-button {
        flex-shrink: 0;
      }

      /* Iconos en azul primario (misma línea visual que columna Acciones en tablas Nebula) */
      .file-actions ::ng-deep lib-button button.mat-mdc-button-base.lib-btn-icon-ghost {
        color: var(--md-sys-color-primary, #0e58a9) !important;
      }

      :host-context(.dark-theme) .file-actions ::ng-deep lib-button button.mat-mdc-button-base.lib-btn-icon-ghost {
        color: var(--md-sys-color-primary, #9ecaff) !important;
      }

      .empty-state {
        padding: 1.5rem 0;
      }
    `,
  ],
})
export class FolderActionsComponent {
  private readonly fileCards = viewChildren<ElementRef<HTMLElement>>('fileCard');

  /** Textos de tooltip para acciones (coinciden con la función de cada icono) */
  readonly tooltipAbrir = 'Abrir el archivo en vista previa.';
  readonly tooltipDescargar = 'Descargar el archivo a su equipo.';
  readonly tooltipAsignar = 'Asignar el documento a un usuario o flujo.';

  /** Misma línea que borde de tarjeta (Nebula primary / M3) */
  readonly fileCardIconColor = 'var(--md-sys-color-primary, #0e58a9)';

  readonly folderName = input('');
  readonly files = input<FileNode[]>([]);
  readonly totalSize = input('');
  /** Ruta del archivo seleccionado en el arbol (resalta la tarjeta). */
  readonly selectedFilePath = input<string | null>(null);
  /** Muestra el botón de asignación solo si el usuario tiene permiso. */
  readonly canAssign = input(false);
  /** Predicado opcional por ruta (p. ej. solo DIP según OAIS). */
  readonly assignablePathPredicate = input<(path: string) => boolean>();

  readonly openFile = output<FileNode>();
  readonly downloadFile = output<FileNode>();
  readonly assignFile = output<FileNode>();

  constructor() {
    effect(() => {
      const path = this.selectedFilePath();
      const list = this.files();
      if (!path || !list.length) {
        return;
      }
      queueMicrotask(() => this.scrollSelectedCardIntoView(path));
    });
  }

  isSelected(file: FileNode): boolean {
    return normalizeRepoPath(this.selectedFilePath()) === normalizeRepoPath(file.path);
  }

  openTooltip(file: FileNode): string {
    if (file.type === 'directory') {
      return 'Abrir carpeta.';
    }
    const ext = (file.extension ?? '').toLowerCase();
    const path = (file.path ?? file.name ?? '').toLowerCase();
    if (ext === 'zip' || path.endsWith('.zip')) {
      return 'Abrir el contenido del ZIP.';
    }
    return this.tooltipAbrir;
  }

  private scrollSelectedCardIntoView(path: string): void {
    const target = normalizeRepoPath(path);
    if (!target) {
      return;
    }
    const index = this.files().findIndex((f) => normalizeRepoPath(f.path) === target);
    const cards = this.fileCards();
    if (index < 0 || index >= cards.length) {
      return;
    }
    cards[index]?.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  getFileIcon(file: FileNode): IconName {
    if (file.type === 'directory') {
      return 'Folder';
    }
    if (!file.name) return 'File';
    const extension = file.name.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, IconName> = {
      // Documentos / texto
      pdf: 'FileText',
      doc: 'FileText',
      docx: 'FileText',
      rtf: 'FileText',
      odt: 'FileText',
      txt: 'FileText',
      md: 'FileText',
      // Hojas de cálculo
      xls: 'Table',
      xlsx: 'Table',
      xlsm: 'Table',
      ods: 'Table',
      csv: 'Table',
      tsv: 'Table',
      // Presentaciones
      ppt: 'Presentation',
      pptx: 'Presentation',
      odp: 'Presentation',
      // Imágenes
      jpg: 'Image',
      jpeg: 'Image',
      png: 'Image',
      gif: 'Image',
      svg: 'Image',
      webp: 'Image',
      bmp: 'Image',
      ico: 'Image',
      tif: 'Image',
      tiff: 'Image',
      heic: 'Image',
      heif: 'Image',
      // Video
      mp4: 'Video',
      mov: 'Video',
      avi: 'Video',
      mkv: 'Video',
      webm: 'Video',
      wmv: 'Video',
      flv: 'Video',
      // Audio
      mp3: 'Music',
      wav: 'Music',
      ogg: 'Music',
      flac: 'Music',
      m4a: 'Music',
      aac: 'Music',
      // Comprimidos
      zip: 'Archive',
      rar: 'Archive',
      '7z': 'Archive',
      gz: 'Archive',
      tar: 'Archive',
      bz2: 'Archive',
      // Código / datos
      html: 'FileText',
      css: 'FileText',
      js: 'FileText',
      ts: 'FileText',
      json: 'FileText',
      xml: 'FileText',
      yml: 'FileText',
      yaml: 'FileText',
      sql: 'FileText',
      log: 'FileText',
    };
    return iconMap[extension || ''] || 'File';
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '-';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  }

  onOpenFile(file: FileNode): void {
    this.openFile.emit(file);
  }

  onDownloadFile(file: FileNode): void {
    this.downloadFile.emit(file);
  }

  onAssignFile(file: FileNode): void {
    this.assignFile.emit(file);
  }

  canAssignFile(file: FileNode): boolean {
    if (!this.canAssign()) {
      return false;
    }
    const predicate = this.assignablePathPredicate();
    if (!predicate) {
      return true;
    }
    return predicate(file.path ?? '');
  }
}
